import { DownOutlined } from "@ant-design/icons";
import { useForm, FormProvider, Controller } from "react-hook-form";
import _ from "lodash";
import { PlaybookStep, PlaybookStepType } from "@prisma/client";
import {
  PlaybookQuestionStepInputZod,
  PlaybookQuestionType,
  PlaybookTaskStepData,
  PlaybookQuestionStepData,
  PlaybookSetPropertyActionStepData,
  PlaybookAddCommentActionStepData,
  PlaybookNotifyStepData,
  PlaybookDelayActionStepData,
  ClientStep,
  ClientStepGroup,
  PlaybookParallelBranchStepData,
  PlaybookGPTQueryStepData,
  PlaybookCodeRunActionStepData,
  ConditionalStepGroupInputZod,
} from "@avenueops/shared-types";
import { MonitorSubscriberBadge } from "components/monitorDetails/subscriberPills/MonitorSubscriberBadge";
import { PlaybookStepAssigneeDropdown } from "components/playbooks/PlaybookStepAssigneeDropdown";
import { Button, OvalLoader, SmartForm, Tooltip, Badge, MarkdownViewer, ToggleGroup, InputOption, Avatar } from "@avenueops/web-components";
import { trpc, trpc2 } from "utils/trpc";
import { humanizeDuration } from "utils/time";
import { EndBlock, GenericBlock } from "./GenericBlocks";
import React, { useMemo, useState } from "react";
import { PlusSmallIcon, CheckIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { IfBranchBlock } from "./steps/IfElseBranchStepInput";
import Link from "next/link";
import { Tree, TreeDataNode } from "antd";
import RunDetailsTable from "components/signals/processPage/RunDetailsTable";
import { PlaybookApprovalStepData } from "@avenueops/shared-types/src/Playbooks/steps/ApprovalStepTypes";
import { useUser } from "context/UserContext";
import CopyableField from "components/CopyableField";
import { NEXT_FRONTEND_URL } from "utils/constants";

const BRANCH_TYPES = new Set<PlaybookStepType>([PlaybookStepType.IF_ELSE_BRANCH, PlaybookStepType.PARALLEL_BRANCH, PlaybookStepType.QUESTION_BRANCH]);

export const TaskBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const { data: completer } = trpc.users.get.useQuery(
    {
      userId: step.completerId!,
    },
    {
      enabled: !!step.completerId,
    },
  );

  const stepData = step.stepData as PlaybookTaskStepData;
  const isOpen = !!step.availableAt && !step.completedAt;
  const executionError = step.error;

  // Hooks
  const utils = trpc.useContext();
  const utils2 = trpc2.useContext();

  // Handlers
  const { mutate: markDone, isLoading } = trpc.processes.markPlaybookTaskDone.useMutation({
    async onSuccess() {
      utils.processes.getOpenPlaybookSteps.invalidate({ processId: step.workflowProcessId });
      utils2.v2.playbookSteps.listStepsByProcess.invalidate({ processId: step.workflowProcessId });
      utils2.v2.playbookSteps.get.invalidate({ stepId: step.id });
      utils2.v2.playbookSteps.list.invalidate();
      utils2.v2.playbookSteps.countActiveTasks.invalidate();
    },
  });
  const markDoneHandler = async () => {
    await markDone({ stepId: step.id, processId: step.workflowProcessId });
  };

  return (
    <GenericBlock decorator={"TASK"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="flex lg:flex-row flex-col justify-between items-center">
        <div className="font-medium text-sm truncate">
          <MarkdownViewer value={stepData.title} />
        </div>
        <div>
          <PlaybookStepAssigneeDropdown step={step} disabled={!!step.completedAt || !step.availableAt} />
        </div>
      </div>
      <div className="text-sm truncate">
        <MarkdownViewer value={stepData.description ?? ""} />
      </div>
      {!isOpen && completer ? (
        <div className="flex gap-1 text-sm items-center">
          <span>Completed by</span>
          <Avatar size="SMALL" src={completer.image ?? undefined} />
          <span>{`${completer.firstName} ${completer.lastName}`}</span>
        </div>
      ) : null}
      {isOpen ? (
        <div className="pt-4 justify-center flex w-full">
          <Button LeadIcon={CheckIcon} onClick={markDoneHandler} submitting={isLoading}>
            Mark as done
          </Button>
        </div>
      ) : null}
    </GenericBlock>
  );
};

export const ApprovalBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const { data: completer } = trpc.users.get.useQuery(
    {
      userId: step.completerId!,
    },
    {
      enabled: !!step.completerId,
    },
  );

  const stepData = step.stepData as PlaybookApprovalStepData;
  const isOpen = !!step.availableAt && !step.completedAt;
  const executionError = step.error;

  // Hooks
  const utils = trpc.useContext();
  const utils2 = trpc2.useContext();
  const user = useUser();

  const approvedResolver = new Set(stepData.allowedResolvers).has(user.email);

  // Handlers
  const { mutate: markDone, isLoading } = trpc.processes.markPlaybookTaskDone.useMutation({
    async onSuccess() {
      utils.processes.getOpenPlaybookSteps.invalidate({ processId: step.workflowProcessId });
      utils2.v2.playbookSteps.listStepsByProcess.invalidate({ processId: step.workflowProcessId });
      utils2.v2.playbookSteps.get.invalidate({ stepId: step.id });
      utils2.v2.playbookSteps.list.invalidate();
      utils2.v2.playbookSteps.countActiveTasks.invalidate();
    },
  });
  const markDoneHandler = async () => {
    await markDone({ stepId: step.id, processId: step.workflowProcessId });
  };

  return (
    <GenericBlock decorator={"APPROVAL"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="font-medium text-sm">
        {stepData.allowedResolvers.length > 1
          ? `Approval required from one of the following: ${stepData.allowedResolvers.join(", ")}`
          : `Approval required from ${stepData.allowedResolvers.join(", ")}`}
      </div>
      <div className="text-sm truncate">
        <MarkdownViewer value={stepData.description ?? ""} />
      </div>
      {!isOpen && completer ? (
        <div className="flex gap-1 text-sm items-center">
          <span>Approved by</span>
          <Avatar size="SMALL" src={completer.image ?? undefined} />
          <span>{`${completer.firstName} ${completer.lastName}`}</span>
        </div>
      ) : null}
      {isOpen && approvedResolver ? (
        <div className="pt-4 justify-center flex w-full">
          <Button LeadIcon={CheckIcon} onClick={markDoneHandler} submitting={isLoading}>
            Approve
          </Button>
        </div>
      ) : null}
    </GenericBlock>
  );
};

export const OutputTree = ({ data }: Record<string, any>) => {
  const constructTreeData = (data: any, prefix: string = ""): TreeDataNode => {
    if (typeof data !== "object" && !Array.isArray(data)) {
      return { title: `${prefix ? prefix + ": " : ""}${data}`, key: prefix };
    }
    if (Array.isArray(data)) {
      return { title: prefix, key: prefix, children: data.map((item, index) => constructTreeData(item, `${prefix ? prefix + "." : ""}${index}`)) };
    }

    return {
      title: prefix,
      key: prefix,
      children: Object.keys(data).map((key) => constructTreeData(data[key], `${prefix ? prefix + "." : ""}${key}`)),
    };
  };
  const treeData = useMemo(
    () =>
      Object.keys(data).map((key) => {
        return constructTreeData(data[key], key);
      }),
    [data],
  );
  return <Tree treeData={treeData} selectable={false} showLine={{ showLeafIcon: false }} switcherIcon={<DownOutlined />} />;
};

export const FormBlock = ({ step, index }: { step: ClientStep; index: number }) => {
  const { data: completer } = trpc.users.get.useQuery(
    {
      userId: step.completerId!,
    },
    {
      enabled: !!step.completerId,
    },
  );

  if (!step.formTemplateId) {
    console.error("Form step has no formTemplateId", { stepId: step.id });

    return null;
  }

  const isOpen = !!step.availableAt && !step.completedAt;
  const executionError = step.error;

  return (
    <GenericBlock decorator={"TASK"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="flex flex-1 flex-col">
        <div className="flex lg:flex-row flex-col justify-between items-center">
          <p className="text-sm">Fill out the form attached to this task</p>
          <div>
            <PlaybookStepAssigneeDropdown step={step} disabled={!!step.completedAt || !step.availableAt} />
          </div>
        </div>
        {isOpen ? (
          <>
            <Link href={`/tasks/${step.id}/form`}>
              <Button>Go to form</Button>
            </Link>
            {step.formTemplate?.public ? (
              <details>
                <summary>Public link</summary>
                <CopyableField value={`${NEXT_FRONTEND_URL}/submit/playbook/${step.id}`} />
              </details>
            ) : null}
          </>
        ) : (
          <div className="cursor-pointer w-fit">
            <details>
              <summary className="text-xs font-medium">Form Response</summary>
              <div className="p-2">
                <div className="p-2">{step.actionOutput ? <OutputTree data={step.actionOutput} /> : "No response yet"}</div>
              </div>
            </details>
          </div>
        )}
        {!isOpen && completer ? (
          <div className="flex gap-1 text-sm items-center w-full">
            <span>Submitted by</span>
            <Avatar size="SMALL" src={completer.image ?? undefined} />
            <span>{`${completer.firstName} ${completer.lastName}`}</span>
          </div>
        ) : null}
      </div>
    </GenericBlock>
  );
};

export const QuestionBlockForm = ({
  step,
  questionType,
  questionOptions,
}: {
  step: PlaybookStep;
  questionType: PlaybookQuestionType;
  questionOptions: InputOption[];
}) => {
  const defaultValues = { stepId: step.id, processId: step.workflowProcessId, response: "" };
  const executionError = step.error;

  // Hooks
  const formMethods = useForm({ defaultValues });
  const utils = trpc.useContext();
  const utils2 = trpc2.useContext();

  const { handleSubmit, formState, control } = formMethods;
  const { isSubmitting } = formState;

  const onSubmit = async (data: { processId: string; stepId: string; response: string }) => {
    await submitResponse(data);
  };

  // Handlers
  const { mutate: submitResponse, isLoading } = trpc.processes.submitPlaybookQuestionResponse.useMutation({
    async onSuccess() {
      utils.processes.getOpenPlaybookSteps.invalidate({ processId: step.workflowProcessId });
      utils2.v2.playbookSteps.listStepsByProcess.invalidate({ processId: step.workflowProcessId });
      utils2.v2.playbookSteps.get.invalidate({ stepId: step.id });
      utils2.v2.playbookSteps.list.invalidate();
      utils2.v2.playbookSteps.countActiveTasks.invalidate();
    },
  });

  return (
    <FormProvider {...formMethods}>
      <div className="flex w-full justify-between gap-4 items-center">
        {questionType === "MULTIPLE_CHOICE" ? (
          <div className="cursor-pointer">
            <Controller
              control={control}
              name={"response"}
              render={({ field: { value, onChange } }) => (
                <ToggleGroup
                  selectedValue={value}
                  onChange={async (value) => {
                    onChange(value);
                    await handleSubmit(onSubmit)();
                  }}
                  name="response"
                  options={questionOptions}
                />
              )}
            />
          </div>
        ) : questionType === "FREEFORM" ? (
          <>
            <SmartForm.TextArea name="response" />
            <div>
              <Button onClick={handleSubmit(onSubmit)} submitting={isSubmitting}>
                Submit
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </FormProvider>
  );
};

export const QuestionBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const { data: completer } = trpc.users.get.useQuery(
    {
      userId: step.completerId!,
    },
    {
      enabled: !!step.completerId,
    },
  );

  const stepData = step.stepData as PlaybookQuestionStepData;
  const actionOutput = step.actionOutput as string | null;
  const selectedOption = actionOutput ?? null;
  const isOpen = !!step.availableAt && !step.completedAt;
  const executionError = step.error;

  // Get the options for the question step
  const { question, questionType, options } = step.stepData as PlaybookQuestionStepInputZod;
  const questionOptions = React.useMemo(() => {
    return (
      options?.map((option) => {
        return { value: option.value, label: option.value };
      }) ?? []
    );
  }, [options]);

  return (
    <GenericBlock decorator={"TASK"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-between items-center">
          <div className="font-medium text-sm truncate">
            <MarkdownViewer value={stepData.question} />
          </div>
          <div>
            <PlaybookStepAssigneeDropdown step={step} disabled={!!step.completedAt || !step.availableAt} />
          </div>
        </div>
        <div className="w-full pt-2">
          {isOpen ? (
            <QuestionBlockForm step={step} questionType={questionType} questionOptions={questionOptions} />
          ) : stepData.questionType === PlaybookQuestionType.MULTIPLE_CHOICE && stepData.options && stepData.options.length > 0 ? (
            <div className="w-fit">
              <ToggleGroup selectedValue={selectedOption ?? undefined} disabled={true} name="response" options={questionOptions} />
            </div>
          ) : stepData.questionType === PlaybookQuestionType.FREEFORM && selectedOption ? (
            <div className="text-sm text-primary-main font-medium">{selectedOption}</div>
          ) : null}
        </div>
        {!isOpen && completer ? (
          <div className="flex gap-1 text-sm items-center w-full">
            <span>Answered by</span>
            <Avatar size="SMALL" src={completer.image ?? undefined} />
            <span>{`${completer.firstName} ${completer.lastName}`}</span>
          </div>
        ) : null}
      </div>
    </GenericBlock>
  );
};

export const SetPropertyActionBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const stepData = step.stepData as PlaybookSetPropertyActionStepData;
  const isOpen = !step.completedAt && !!step.availableAt;
  const executionError = step.error;

  const propertyChange = stepData.propertyChange;
  const propertyChangeType = _.capitalize(propertyChange.type);
  const propertyChangeValue =
    propertyChange.type === "status"
      ? propertyChange.statusLabel
      : propertyChange.type === "priority"
      ? _.capitalize(propertyChange.priority)
      : propertyChange.assigneeTemplate;

  return (
    <GenericBlock decorator={isOpen ? "ACTIVE" : "DEFAULT"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="text-sm">
        Set <span className="font-semibold border-b border-dashed border-gray-300 pb-[1.5px]">{propertyChangeType}</span> to{" "}
        <span className="font-semibold border-b border-dashed border-gray-300 pb-[1.5px]">{propertyChangeValue}</span>
      </div>
    </GenericBlock>
  );
};

export const AddCommentActionBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const stepData = step.stepData as PlaybookAddCommentActionStepData;
  const isOpen = !step.completedAt && !!step.availableAt;
  const executionError = step.error;

  return (
    <GenericBlock decorator={isOpen ? "ACTIVE" : "DEFAULT"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="text-sm flex flex-wrap gap-1">
        Add comment
        <div className="font-semibold">{stepData.comment}</div>
      </div>
    </GenericBlock>
  );
};

export const CodeRunActionBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const stepData = step.stepData as PlaybookCodeRunActionStepData;
  const isOpen = !step.completedAt && !!step.availableAt;
  const executionError = step.error;

  return (
    <GenericBlock decorator={isOpen ? "ACTIVE" : "DEFAULT"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="text-sm flex flex-wrap gap-1">
        Ran code block
        {step.completedAt ? (
          <>
            <span> with output</span>
            <div className="font-semibold">{JSON.stringify(step.actionOutput, null, 2)}</div>
          </>
        ) : null}
      </div>
    </GenericBlock>
  );
};

export const NotifyBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const stepData = step.stepData as PlaybookNotifyStepData;
  const isOpen = !step.completedAt && !!step.availableAt;
  const executionError = step.error;

  return (
    <GenericBlock decorator={isOpen ? "ACTIVE" : "DEFAULT"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="flex flex-wrap gap-3 items-center">
        <span className="text-sm border-b border-dashed border-gray-300 pb-[1.5px]">Send to</span>
        <MonitorSubscriberBadge recipient={stepData.manualNotification} />
      </div>
    </GenericBlock>
  );
};

export const DelayActionBlock = ({ step, index }: { step: PlaybookStep; index: number }) => {
  const stepData = step.stepData as PlaybookDelayActionStepData;
  const isOpen = !step.completedAt && !!step.availableAt;
  const executionError = step.error;

  return (
    <GenericBlock decorator={isOpen ? "ACTIVE" : "DEFAULT"} isComplete={!!step.completedAt} completedAt={step.completedAt} executionError={executionError}>
      <div className="text-sm">
        Wait for{" "}
        <span className="font-semibold border-b border-dashed border-gray-300 pb-[1.5px]">
          {humanizeDuration(stepData.duration * 1000, ["y", "mo", "d", "h", "m", "s"])}
        </span>
      </div>
    </GenericBlock>
  );
};

export const MoreItemsBlock = ({ num }: { num: number }) => {
  return (
    <GenericBlock decorator="SOLID" bottomLineStyle="BLANK">
      <div className="select-none py-2">
        <Badge LeadIcon={PlusSmallIcon}>
          <span className="font-medium">
            {num} step{num !== 1 ? "s" : ""}
          </span>
        </Badge>
      </div>
    </GenericBlock>
  );
};

export const ParallelBranchBlock = ({ step, groups, steps }: { steps: ClientStep[]; step: ClientStep; groups: ClientStepGroup[] }) => {
  // Find all groups that are children of this step
  const allGroups = groups?.filter((group) => group.parentStepId === step.id);
  const stepData = step.stepData as PlaybookParallelBranchStepData;
  const executionError = step.error;

  return !step.availableAt ? ( // Before execution
    <GenericBlock decorator="NONE" bottomLineStyle="THICK" topLineStyle="THICK">
      {stepData.title ? <div className="text-sm font-medium mb-2">{stepData.title}</div> : null}
      {allGroups?.map((branch, index) => (
        <div key={branch.id} className="py-1 text-sm flex flex-col gap-1">
          {stepData.branchLabels?.length > index && stepData.branchLabels[index] ? (
            <Badge type="NEUTRAL" size="SMALL">
              <span className="font-semibold">{stepData.branchLabels[index]}</span>
            </Badge>
          ) : null}
          <div className="select-none">
            <BranchPreview step={step} steps={steps} groups={groups} branch={branch} />
          </div>
        </div>
      ))}
    </GenericBlock>
  ) : (
    // During and after execution of the branch
    <GenericBlock decorator="NONE" bottomLineStyle="THICK" topLineStyle="THICK" isComplete={!!step.completedAt} executionError={executionError}>
      {stepData.title ? <div className="text-sm font-medium mb-2">{stepData.title}</div> : null}
      {allGroups?.map((branch, index) => (
        <div key={branch.id}>
          {stepData.branchLabels?.length > index && stepData.branchLabels[index] ? (
            <Badge type="NEUTRAL" size="SMALL">
              <span className="font-semibold">{stepData.branchLabels[index]}</span>
            </Badge>
          ) : null}
          <ProcessPlaybook steps={steps} groups={groups} parentStepGroupId={branch.id} parentStepGroupCompleted={!!step.completedAt} />
        </div>
      ))}
    </GenericBlock>
  );
};

const BranchPreview = ({ branch, steps, groups, step }: { steps: ClientStep[]; groups: ClientStepGroup[]; step: ClientStep; branch: ClientStepGroup }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  return (
    <div
      onClick={() => {
        setExpanded(!expanded);
      }}
      className="text-gray-500 text-sm w-full flex cursor-pointer flex-col"
    >
      <div className="ml-[35px]">
        <Badge LeadIcon={!expanded ? ArrowsPointingOutIcon : ArrowsPointingInIcon} type="WARNING">
          {branch.steps.length} step{branch.steps.length !== 1 ? "s" : ""} in branch{" "}
        </Badge>
      </div>
      {/* Previewing */}
      {expanded ? (
        <ProcessPlaybook previewAll={true} steps={steps} groups={groups} parentStepGroupId={branch.id} parentStepGroupCompleted={!!step.completedAt} />
      ) : null}
    </div>
  );
};

export const IfElseBranchBlock = ({ steps, step, groups }: { steps: ClientStep[]; step: ClientStep; groups: ClientStepGroup[] }) => {
  // Get the first available step that is a child of this step
  const firstAvailableStep = React.useMemo(() => {
    return steps.find((currStep) => !currStep.completedAt && currStep.availableAt && currStep.parentStepGroup?.parentStepId === step.id);
  }, [step.id, steps]);

  // Find all groups that are children of this step
  const allGroups = groups?.filter((group) => group.parentStepId === step.id);

  const executedBranch = !step.completedAt
    ? groups?.find((group) => firstAvailableStep?.parentStepGroupId === group.id) // Find the group of the first available child step (during execution)
    : groups?.find((group) => group.parentStepId === step.id && group.completedAt); // Find the child group that was completed (after execution)
  const executionError = step.error;

  return !step.availableAt ? ( // Before execution
    <>
      {allGroups?.map((branch) => (
        <div key={branch.id}>
          <GenericBlock decorator="SOLID" bottomLineStyle="NESTED">
            <div className="py-2">
              <IfBranchBlock step={{ condition: branch.condition, steps: branch.steps } as ConditionalStepGroupInputZod} />
            </div>
          </GenericBlock>
          <div className={`ml-[37.5px] border-l border-gray-300 select-none`}>
            <BranchPreview step={step} steps={steps} groups={groups} branch={branch} />
          </div>
        </div>
      ))}
    </>
  ) : step.availableAt && executedBranch ? ( // During and after execution of the branch
    <div className={clsx({ "bg-gray-50": !!step.completedAt })}>
      <GenericBlock decorator="DEFAULT" bottomLineStyle="NESTED" isComplete={!!step.completedAt} executionError={executionError}>
        <div className="py-2">
          <IfBranchBlock step={{ condition: executedBranch.condition, steps: executedBranch.steps } as ConditionalStepGroupInputZod} />
        </div>
      </GenericBlock>
      <div className={`ml-[37.5px] border-l ${step.completedAt ? "border-primary-main" : "border-gray-300"}`}>
        <ProcessPlaybook steps={steps} groups={groups} parentStepGroupId={executedBranch.id} parentStepGroupCompleted={!!step.completedAt} />
      </div>
    </div>
  ) : null;
};

export const QueryGPTActionBlock = ({ step }: { step: PlaybookStep }) => {
  const gptQuery = (step.stepData as PlaybookGPTQueryStepData)?.query;
  const gptResponse = step.actionOutput as string | null;
  const isOpen = !step.completedAt && !!step.availableAt;
  const executionError = step.error;
  const [showMore, setShowMore] = React.useState(false);

  return (
    <GenericBlock decorator={isOpen ? "ACTIVE" : "DEFAULT"} isComplete={!!step.completedAt} completedAt={step.completedAt}>
      {step.completedAt ? (
        <div className="text-sm">
          {gptResponse && !executionError ? (
            <div className="relative pb-5">
              <p className="text-gray-900 font-medium">Prompt</p>
              <p>{gptQuery}</p>
              <p className="py-2 text-gray-900 font-medium">GPT Completion</p>
              <div
                className={` ${!showMore ? "max-h-20 overflow-hidden" : ""}`}
                style={
                  !showMore
                    ? {
                        WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 100%)",
                        maskImage: "linear-gradient(to bottom, black 0%, transparent 100%)",
                      }
                    : {}
                }
              >
                {/** Get the first 50 words of the response */}
                <MarkdownViewer value={gptResponse} />
              </div>
              <div
                className="absolute bottom-0 right-0 text-primary-main cursor-pointer"
                onClick={() => {
                  setShowMore(!showMore);
                }}
              >
                {showMore ? "Show less" : "Show more"}
              </div>
            </div>
          ) : executionError ? (
            <p className="text-red-500">{executionError}</p>
          ) : (
            <p className="text-red-500">An unknown error occured while prompting OpenAI</p>
          )}
        </div>
      ) : (
        <div className="flex">
          <div className="text-sm">Waiting for GPT response</div>
          <OvalLoader size={10} />
        </div>
      )}
    </GenericBlock>
  );
};

export const DatasourceQueryActionBlock = ({ step }: { step: PlaybookStep }) => {
  const query = (step.stepData as PlaybookGPTQueryStepData)?.query;
  const response = step.actionOutput;
  const isOpen = !step.completedAt && !!step.availableAt;
  const executionError = step.error;

  return (
    <GenericBlock decorator={isOpen ? "ACTIVE" : "DEFAULT"} isComplete={!!step.completedAt} completedAt={step.completedAt}>
      {step.completedAt ? (
        <div className="text-sm">
          {response && !executionError ? (
            <div>
              <details>
                <summary className="text-gray-900 font-medium">Query Result</summary>
                <RunDetailsTable dataDiff={response as unknown[]} />
              </details>
            </div>
          ) : executionError ? (
            <p className="text-red-500">{executionError}</p>
          ) : (
            <p className="text-red-500">An unknown error occured while querying the datasource</p>
          )}
        </div>
      ) : (
        <div className="flex">
          <div className="text-sm">Waiting for datasource response</div>
          <OvalLoader size={10} />
        </div>
      )}
    </GenericBlock>
  );
};

export const ProcessPlaybookBlock = ({
  steps,
  step,
  index,
  groups,
  firstAvailableStep,
}: {
  steps: ClientStep[];
  step: ClientStep;
  index: number;
  groups: ClientStepGroup[];
  firstAvailableStep?: ClientStep;
}) => {
  switch (step.type) {
    case PlaybookStepType.TASK:
      return <TaskBlock step={step} index={index} />;
    case PlaybookStepType.APPROVAL:
      return <ApprovalBlock step={step} index={index} />;
    case PlaybookStepType.FORM:
      return <FormBlock step={step} index={index} />;
    case PlaybookStepType.QUESTION:
      return <QuestionBlock step={step} index={index} />;
    case PlaybookStepType.SET_PROPERTY_ACTION:
      return <SetPropertyActionBlock step={step} index={index} />;
    case PlaybookStepType.NOTIFY_ACTION:
      return <NotifyBlock step={step} index={index} />;
    case PlaybookStepType.CODE_RUN_ACTION:
      return <CodeRunActionBlock step={step} index={index} />;
    case PlaybookStepType.ADD_COMMENT_ACTION:
      return <AddCommentActionBlock step={step} index={index} />;
    case PlaybookStepType.DELAY_ACTION:
      return <DelayActionBlock step={step} index={index} />;
    case PlaybookStepType.IF_ELSE_BRANCH:
      return <IfElseBranchBlock steps={steps} step={step} groups={groups} />;
    case PlaybookStepType.PARALLEL_BRANCH:
      return <ParallelBranchBlock steps={steps} step={step} groups={groups} />;
    case PlaybookStepType.QUERY_GPT_ACTION:
      return <QueryGPTActionBlock step={step} />;
    case PlaybookStepType.DATASOURCE_QUERY_ACTION:
      return <DatasourceQueryActionBlock step={step} />;
    // Compound blocks
    case PlaybookStepType.QUESTION_BRANCH:
    case PlaybookStepType.CHECKLIST:
    default:
      return null as never;
  }
};

export const ProcessPlaybook = ({
  steps,
  groups,
  parentStepGroupId = null, // null if this is the main branch
  parentStepGroupCompleted = false,
  previewAll = false,
}: {
  steps: ClientStep[];
  groups: ClientStepGroup[];
  parentStepGroupId?: string | null;
  parentStepGroupCompleted?: boolean;
  previewAll?: boolean;
}) => {
  // Compute the number of open steps in the parent branch
  const numOpenItems = React.useMemo(() => {
    const result = steps.filter(
      (step) => !step.completedAt && step.parentStepGroupId === parentStepGroupId && step.availableAt, // Filter for steps on the parent branch
    ).length;
    return result;
  }, [parentStepGroupId, steps]);

  // Render all of the active & completed steps or branches in the parent branch
  return (
    <div>
      <div className="relative flex flex-col">
        {steps.map((step, index) =>
          (previewAll || step.completedAt || step.availableAt || BRANCH_TYPES.has(step.type)) && step.parentStepGroupId === parentStepGroupId ? (
            <ProcessPlaybookBlock steps={steps} key={step.id} step={step} index={step.index} groups={groups} />
          ) : null,
        )}
        {!previewAll && numOpenItems > 1 ? (
          <MoreItemsBlock num={numOpenItems - 1} />
        ) : (
          <EndBlock nested={!!parentStepGroupId} isComplete={parentStepGroupCompleted} />
        )}
      </div>
    </div>
  );
};
